// src/components/AdminPanel/AdminUserSlots.js
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';


function deriveStatus(planConnections = []) {
  if (!planConnections.length) return "none";

  const subs = planConnections.filter(
    (p) => (p.type || "").toLowerCase() === "subscription"
  );
  if (!subs.length) return "none";

  const activeOrTrial = subs.find((p) => {
    const s = (p.status || "").toLowerCase();
    return s === "active" || s === "trialing";
  });
  if (activeOrTrial) {
    return activeOrTrial.status.toLowerCase() === "trialing"
      ? "trialing"
      : "active";
  }

  const cancelled = subs.find((p) => {
    const s = (p.status || "").toLowerCase();
    return s === "cancelled" || s === "canceled";
  });
  return cancelled ? "cancelled" : "none";
}

/**
 * AdminUserSlots.
 *
 * Expects a prop: { adminToken }
 * Fetches user_slots and allows filtering by gathering date, city, area, and theme.
 * When any filter is set, all four must be set to show results.
 * Also reads URL params to pre-populate filters when navigating from User-Gatherings.
 */
function AdminUserSlots({ adminToken }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [themeFilter, setThemeFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [simpleView, setSimpleView] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  const uniqueThemes = [...new Set(records.map(r => r.theme_name).filter(Boolean))].sort();
  const uniqueCities = [...new Set(records.map(r => r.city).filter(Boolean))].sort();

  const shouldShowData = dataLoaded;

  async function fetchUserSlots() {
    if (!adminToken) return;
    try {
      setLoading(true);
      // Fetch slots (future only) and Memberstack plan data in parallel
      const [data, memberstackResult] = await Promise.all([
        adminApi.getUserSlots({ future_only: true }, adminToken),
        adminApi.getMembersWithPlans(adminToken),
      ]);

      // Build memberstack_id → plan status lookup
      const planLookup = new Map();
      const members = memberstackResult?.data || [];
      members.forEach(m => planLookup.set(m.id, deriveStatus(m.planConnections || [])));

      // memberstack_id is now included in each slot record from the backend
      const recordsWithSubscription = data.map(record => {
        const subscriptionStatus = record.memberstack_id
          ? planLookup.get(record.memberstack_id) || "none"
          : "none";
        const displayStatus = subscriptionStatus === "none"
          ? "ticket (no subscription)"
          : subscriptionStatus === "cancelled"
            ? "CANCELLED"
            : subscriptionStatus;
        return { ...record, subscription_status: displayStatus };
      });

      setRecords(recordsWithSubscription);
      setDataLoaded(true);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (adminToken) {
      fetchUserSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken]);

  // Apply URL params to filters after data is loaded
  useEffect(() => {
    if (dataLoaded && records.length > 0) {
      const urlDate = searchParams.get('date');
      const urlTheme = searchParams.get('theme');

      if (urlDate || urlTheme) {
        if (!dateFilter && !themeFilter) {
          if (urlDate) setDateFilter(urlDate);
          if (urlTheme) setThemeFilter(urlTheme);
          setSearchParams({ tab: 'userSlots' });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoaded, records.length]);

  // Filter logic — apply whichever filters are set; show all future slots if none
  const filteredRecords = records.filter(r => {
    if (dateFilter) {
      const recDate = new Date(r.date_and_time).toISOString().split('T')[0];
      if (recDate !== dateFilter) return false;
    }
    if (themeFilter && (r.theme_name || '') !== themeFilter) return false;
    if (cityFilter && (r.city || '') !== cityFilter) return false;
    return true;
  });

  const clearAllFilters = () => {
    setDateFilter('');
    setThemeFilter('');
    setCityFilter('');
  };

  const copyRowToClipboard = async (record) => {
    // Create tab-separated values for easy Excel pasting
    const rowData = [
      record.email,
      record.first_name,
      record.last_name,
      record.city || record.city_name || '—',
      simpleView
        ? new Date(record.date_and_time).toLocaleDateString()
        : new Date(record.date_and_time).toLocaleString(),
      record.area === 'Liverpool Street / Bishopsgate' ? 'Liv Street' : record.area,
      record.theme_name || 'Casual',
      record.subscription_status,
    ].join('\t'); // Tab-separated for Excel compatibility

    try {
      await navigator.clipboard.writeText(rowData);
      // You could add a toast notification here if desired
    } catch (err) {
      console.error('Failed to copy: ', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = rowData;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  return (
    <div>
      <style>{`
        @media (max-width: 768px) {
          .admin-user-slots-container {
            padding: 2px !important;
          }
          .admin-user-slots-table {
            min-width: 100% !important;
            font-size: 0.65rem !important;
          }
          .admin-user-slots-table th,
          .admin-user-slots-table td {
            padding: 0.2rem !important;
            font-size: 0.65rem !important;
            word-wrap: break-word;
            white-space: normal;
            line-height: 1.2;
            vertical-align: top;
          }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <h2>User Slots</h2>

      {/* View All Button and Filters */}
      <div
        style={{
          marginBottom: '0.75rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem'
        }}
      >
        {/* View All Button - COMMENTED OUT
        <div>
          <button
            onClick={() => setViewAll(prev => !prev)}
            style={{
              padding: '10px 20px',
              backgroundColor: viewAll ? '#27ae60' : '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '14px'
            }}
          >
            {viewAll ? '✓ Viewing All Slots (Past & Future)' : '📋 View All Slots'}
          </button>
          {viewAll && (
            <span style={{ marginLeft: '1rem', color: '#666', fontSize: '0.9rem' }}>
              Showing past and future slots
            </span>
          )}
        </div>
        */}

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 auto', minWidth: '160px', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label htmlFor="dateFilter" style={{ fontSize: '0.85rem', fontWeight: '600' }}>
              Gathering Date:
            </label>
            <input
              type="date"
              id="dateFilter"
              value={dateFilter}
              onChange={e => setDateFilter(e.target.value)}
              style={{ padding: '0.5rem', fontSize: '0.9rem', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>

          <div style={{ flex: '1 1 auto', minWidth: '120px', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label htmlFor="themeFilter" style={{ fontSize: '0.85rem', fontWeight: '600' }}>
              Theme:
            </label>
            <select
              id="themeFilter"
              value={themeFilter}
              onChange={e => setThemeFilter(e.target.value)}
              style={{ padding: '0.5rem', fontSize: '0.9rem', borderRadius: '4px', border: '1px solid #ccc' }}
            >
              <option value="">Select Theme</option>
              {uniqueThemes.map(theme => (
                <option key={theme} value={theme}>{theme}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: '1 1 auto', minWidth: '120px', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label htmlFor="cityFilter" style={{ fontSize: '0.85rem', fontWeight: '600' }}>
              City:
            </label>
            <select
              id="cityFilter"
              value={cityFilter}
              onChange={e => setCityFilter(e.target.value)}
              style={{ padding: '0.5rem', fontSize: '0.9rem', borderRadius: '4px', border: '1px solid #ccc' }}
            >
              <option value="">All Cities</option>
              {uniqueCities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>

          {(dateFilter || themeFilter || cityFilter) && (
            <button
              onClick={clearAllFilters}
              style={{
                alignSelf: 'flex-end',
                padding: '0.5rem 1rem',
                fontSize: '0.85rem',
                borderRadius: '4px',
                border: '1px solid #ccc',
                backgroundColor: '#f0f0f0',
                cursor: 'pointer',
                minWidth: '100px'
              }}
            >
              Clear All Filters
            </button>
          )}
        </div>


        {dataLoaded && !loading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => setSimpleView(prev => !prev)}
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.85rem',
                borderRadius: '4px',
                border: '1px solid #ccc',
                backgroundColor: '#f0f0f0',
                cursor: 'pointer'
              }}
            >
              {simpleView ? 'Full View' : 'Simple View'}
            </button>
            <span style={{ fontSize: '0.9rem', fontWeight: '600' }}>count: {filteredRecords.length}</span>
          </div>
        )}
      </div>

      {loading && (
        <div style={{ padding: '3rem', textAlign: 'center' }}>
          <div style={{
            display: 'inline-block',
            width: '56px',
            height: '56px',
            border: '6px solid #e0e0e0',
            borderTopColor: '#3498db',
            borderRadius: '50%',
            animation: 'spin 0.9s linear infinite',
            marginBottom: '1rem'
          }} />
          <p style={{ fontSize: '1.1rem', color: '#555', margin: 0 }}>Loading user slots…</p>
        </div>
      )}
      {!loading && error && <p style={{ color: 'red', padding: '1rem' }}>{error}</p>}

      {/* Show table only if data should be displayed */}
      {shouldShowData && dataLoaded && !loading && (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table className="admin-user-slots-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: window.innerWidth <= 768 ? '100%' : '600px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #ccc' }}>
                <th style={{ padding: '4px', width: '40px', fontSize: '0.85rem', position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 1 }}>Copy</th>
                <th style={{ padding: simpleView ? '4px' : '8px', fontSize: '0.85rem' }}>Email</th>
                <th style={{ padding: simpleView ? '4px' : '8px', fontSize: '0.85rem' }}>First&nbsp;Name</th>
                <th style={{ padding: simpleView ? '4px' : '8px', fontSize: '0.85rem' }}>Last&nbsp;Name</th>
                <th style={{ padding: simpleView ? '4px' : '8px', fontSize: '0.85rem' }}>City</th>
                <th style={{ padding: simpleView ? '4px' : '8px', fontSize: '0.85rem' }}>
                  {simpleView ? 'Date' : 'Gathering Date-Time'}
                </th>
                <th style={{ padding: simpleView ? '4px' : '8px', fontSize: '0.85rem' }}>Area</th>
                <th style={{ padding: simpleView ? '4px' : '8px', fontSize: '0.85rem' }}>Theme</th>
                <th style={{ padding: simpleView ? '4px' : '8px', fontSize: '0.85rem' }}>Subscription</th>
              </tr>
            </thead>
          <tbody>
            {filteredRecords.map((r, idx) => (
              <tr
                key={`${r.email}-${r.date_and_time}`}
                style={{
                  backgroundColor: idx % 2 === 0 ? '#fff' : '#f5f5f5',
                  borderBottom: '1px solid #ccc'
                }}
              >
                <td style={{ padding: '4px', textAlign: 'center', position: 'sticky', left: 0, backgroundColor: idx % 2 === 0 ? '#fff' : '#f5f5f5', zIndex: 1 }}>
                  <button
                    onClick={() => copyRowToClipboard(r)}
                    style={{
                      padding: '2px 6px',
                      fontSize: '12px',
                      border: '1px solid #ccc',
                      borderRadius: '3px',
                      backgroundColor: '#f8f9fa',
                      cursor: 'pointer',
                      lineHeight: '1'
                    }}
                    title="Copy row data"
                  >
                    📋
                  </button>
                </td>
                <td style={{ padding: simpleView ? '4px' : '8px', fontSize: '0.85rem' }}>{r.email}</td>
                <td style={{ padding: simpleView ? '4px' : '8px', fontSize: '0.85rem' }}>{r.first_name}</td>
                <td style={{ padding: simpleView ? '4px' : '8px', fontSize: '0.85rem' }}>{r.last_name}</td>
                <td style={{ padding: simpleView ? '4px' : '8px', fontSize: '0.85rem' }}>
                  {r.city || r.city_name || '—'}
                </td>
                <td style={{ padding: simpleView ? '4px' : '8px', fontSize: '0.85rem' }}>
                  {simpleView
                    ? new Date(r.date_and_time).toLocaleDateString()
                    : new Date(r.date_and_time).toLocaleString()}
                </td>
                <td style={{ padding: simpleView ? '4px' : '8px', fontSize: '0.85rem' }}>
                  {r.area === 'Liverpool Street / Bishopsgate'
                    ? 'Liv Street'
                    : r.area}
                </td>
                <td style={{ padding: simpleView ? '4px' : '8px', fontSize: '0.85rem' }}>
                  {r.theme_name || 'Casual'}
                </td>
                <td style={{ padding: simpleView ? '4px' : '8px', fontSize: '0.85rem' }}>
                  {r.subscription_status}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

export default AdminUserSlots;